import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import type { PlaybackCommandAction } from "../../types/popout";

const RATE_CYCLE = [0.5, 1, 1.5, 2];

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface MiniPlaybackControlsProps {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  playbackRate: number;
  playerClosed?: boolean;
  onCommand: (cmd: PlaybackCommandAction) => void;
}

export default function MiniPlaybackControls({
  currentTime,
  duration,
  isPlaying,
  playbackRate,
  playerClosed = false,
  onCommand,
}: Readonly<MiniPlaybackControlsProps>) {
  const handlePlayPause = () => {
    if (isPlaying) {
      onCommand({ action: "pause" });
    } else {
      onCommand({ action: "play" });
    }
  };

  const handleSeek = (_event: Event, value: number | number[]) => {
    onCommand({ action: "seek", time: value as number });
  };

  const handleRateCycle = () => {
    const currentIndex = RATE_CYCLE.indexOf(playbackRate);
    const nextIndex = currentIndex === -1 || currentIndex === RATE_CYCLE.length - 1
      ? 0
      : currentIndex + 1;
    onCommand({ action: "setRate", rate: RATE_CYCLE[nextIndex] });
  };

  const formatRate = (rate: number) => `${rate}x`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        width: "100%",
      }}
    >
      {playerClosed ? (
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
          Player closed
        </Typography>
      ) : null}

      <IconButton
        aria-label={isPlaying ? "Pause" : "Play"}
        size="small"
        onClick={handlePlayPause}
        disabled={playerClosed}
      >
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>

      <Slider
        size="small"
        min={0}
        max={duration || 1}
        value={currentTime}
        onChange={handleSeek}
        disabled={playerClosed}
        sx={{ flex: 1, mx: 1 }}
      />

      <Typography
        variant="caption"
        sx={{ fontFamily: "monospace", flexShrink: 0, minWidth: "3.5em", textAlign: "center" }}
      >
        {formatTime(currentTime)}
      </Typography>

      <Typography
        variant="caption"
        sx={{ fontFamily: "monospace", flexShrink: 0, minWidth: "3.5em", textAlign: "center" }}
        color="text.secondary"
      >
        {formatTime(duration)}
      </Typography>

      <Button
        size="small"
        variant="text"
        onClick={handleRateCycle}
        disabled={playerClosed}
        sx={{ minWidth: "3em", fontFamily: "monospace", fontSize: "0.7rem", flexShrink: 0 }}
      >
        {formatRate(playbackRate)}
      </Button>
    </Box>
  );
}
